<template>
  <v-data-table
    :headers="selectedHeaders"
    :items="data"
    disable-pagination
    hide-default-header
    hide-default-footer
    item-key="name"
    :loading="isLoading"
    :class="tableHeight"
    :expanded.sync="expanded"
    :show-expand="showExpand"
    :single-expand="true"
    :show-select="select"
    :no-data-text="noItemsText"
    mobile-breakpoint="760"
  >
    <template #header="{ props, isMobile }">
      <thead v-if="!isMobile">
        <draggable
          v-model="selectedHeaders"
          tag="tr"
          class="dataTableHeaders"
          @change="headersOrderUpdated($event)"
        >
          <th
            v-for="header in props.headers"
            v-show="displayColumn(header)"
            :key="header.name"
            class="header-draggable"
            :style="{ textAlign: header.align || 'left' }"
          >
            <data-table-header-cell
              :class="header.class || ''"
              :header="header"
              :display-select-list="getSelectList"
              :select-list-items="selectListFilter"
              :select-all-rows="selectAllRows"
              :show-actions="showActions"
              :show-select="showSelect"
              @update-order="orderUpdated($event)"
              @handle-select-all="handleValueInAllRows($event)"
              @filter-select-list="filterSelectList($event)"
            />
          </th>
        </draggable>
      </thead>
    </template>
    <template
      #item="{
        item: row,
        index: rowIndex,
        headers: headersTable,
        expand: handleExpand,
        isExpanded: expand,
        isMobile
      }"
    >
      <context-menu
        v-if="isContextMenu"
        :ref="createContextRef(row.id)"
      >
        <context-menu-item
          v-for="(item, posItem) in contextItems"
          v-show="getBool(item.show)"
          :key="posItem"
          :text="getActionOptionName(item, row)"
          :icon="getActionOptionIcon(item, row)"
          @click="contextMenuClick(item, row)"
        />
      </context-menu>
      <tr
        v-if="!isMobile"
        @contextmenu="showContextMenu($event, row.id)"
      >
        <td
          v-for="(header, key) in headersTable"
          :key="generateKey(rowIndex, row.id, key)"
          :align="header.align || 'left'"
          :width="header.width"
          :class="header.class"
        >
          <data-table-body-components
            :action-options="actionOptions"
            :data="data"
            :expand="expand"
            :generate-key="generateKey"
            :get-action-option-icon="getActionOptionIcon"
            :get-action-option-name="getActionOptionName"
            :handle-expand="handleExpand"
            :header="header"
            :hide-delete="hideDelete"
            :hide-edit="hideEdit"
            :on-click-delete="onClickDelete"
            :is-select="isSelect"
            :on-click-edit="onClickEdit"
            :row="row"
            :row-index="rowIndex"
            :selected="selected"
            :select-rows="selectRows"
            :show-actions="showActions"
            :show-select="showSelect"
            :all-column-width="allColumnWidth"
            :get-prop-by-string="getPropByString"
            :updated-page="updatedPage"
            :header-index="key"
          />
        </td>
      </tr>
      <v-card
        v-else
        class="elevation-2 mt-1"
      >
        <v-card-text>
          <form-group>
            <div
              v-for="(header, key) in headersTable"
              v-show="displayColumn(header)"
              :key="'mobile' + generateKey(rowIndex, row.id, key)"
              class="pa-2 d-flex justify-space-between align-center"
              :class="computedClass(header)"
              :width="header.width || '100px'"
            >
              <strong v-show="displayColumn(header)">
                {{ !isActionsHeader(header) ? !isSelect(header) ? header.text : '' : 'Ações' }}
              </strong>

              <data-table-body-components
                :action-options="actionOptions"
                :data="data"
                :expand="expand"
                :generate-key="generateKey"
                :get-action-option-icon="getActionOptionIcon"
                :get-action-option-name="getActionOptionName"
                :handle-expand="handleExpand"
                :header="header"
                :hide-delete="hideDelete"
                :hide-edit="hideEdit"
                :on-click-delete="onClickDelete"
                :is-select="isSelect"
                :on-click-edit="onClickEdit"
                :row="row"
                :row-index="rowIndex"
                :selected="selected"
                :select-rows="selectRows"
                :show-actions="showActions"
                :show-select="showSelect"
                :all-column-width="allColumnWidth"
                :get-prop-by-string="getPropByString"
                :updated-page="updatedPage"
                :header-index="key"
              />
            </div>
          </form-group>
        </v-card-text>
      </v-card>
    </template>
    <template #expanded-item="{ item: row, headers: headersTable }">
      <tr class="expand_shadow">
        <td
          class="py-2"
          :colspan="headersTable.length"
        >
          <v-col cols="12">
            <component
              :is="expandComponent"
              :row="row"
              :data="data"
              :row-index="findRowItemIndex(row)"
              :headers="headersTable"
              :page="pagination.page"
              :limit="pagination.limit"
            />
          </v-col>
        </td>
      </tr>
    </template>
    <template
      v-if="!hideTableBottom"
      v-slot:footer
    >
      <v-container fluid>
        <v-row>
          <v-col
            style="height: 60px"
            class="d-flex align-center"
          >
            <div class="body1 interface200 d-flex align-center">
              <span> Registros por página: </span>
              <span>
                <v-select
                  v-model="pagination.limit"
                  class="rowsPerPageSelect mx-2 pt-3"
                  :items="[15, 30, 60]"
                  dense
                />
              </span>
              <span> {{ totalItemsText }} de {{ totalItems }} </span>
            </div>
          </v-col>
          <v-col
            style="height: 60px"
            class="d-flex align-center justify-center"
          >
            <div class="">
              <v-pagination
                v-model="pagination.page"
                :length="pages"
                :total-visible="7"
                circle
              />
            </div>
          </v-col>
          <v-col
            style="height: 60px"
            class="d-flex align-center justify-end"
          >
            <div>
              <data-table-config
                v-model="selectedHeaders"
                :items="headers"
                :table-headers-key="tableName"
                :display-column-content="allColumnWidth"
                @handle-display="handleAllColumnWidth($event)"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-data-table>
</template>

<script>
import DataTableConfig from 'Arch/components/data-table/DataTableConfig.vue';
import DataTableCpfCnpjColumn from 'Arch/components/data-table/DataTableColumns/DataTableCpfCnpjColumn.vue';
import DataTableDateColumn from 'Arch/components/data-table/DataTableColumns/DataTableDateColumn.vue';
import DataTableHeaderCell from 'Arch/components/data-table/DataTableHeaderCell.vue';
import DataTableService from 'Arch/components/data-table/DatatableService';
import DataTableTextColumn from 'Arch/components/data-table/DataTableColumns/DataTableTextColumn.vue';
import DataTableTransformValueColumn from 'Arch/components/data-table/DataTableColumns/DataTableTransformValueColumn.vue';
import DataTableCurrencyColumn from 'Arch/components/data-table/DataTableColumns/DataTableCurrencyColumn.vue';
import DataTableImgRow from 'Arch/components/data-table/DataTableColumns/DataTableImgRow.vue';
import DataTableStatusColumn from 'Arch/components/data-table/DataTableColumns/DataTableStatusColumn.vue';
import Draggable from 'vuedraggable';
import Get from 'lodash.get';
import { ContextMenu, ContextMenuItem, ContextMenuMixin } from 'Arch/components/ContextMenu';
import SelectFilterList from '../crud/components/SelectFilterList.vue';
import FormGroup from '../crud/components/FormGroup.vue';
import DataTableBodyComponents from './DataTableBodyComponents.vue';

export default {
  name: 'DataTable',
  components: {
    DataTableConfig,
    DataTableCpfCnpjColumn,
    DataTableDateColumn,
    DataTableHeaderCell,
    DataTableTextColumn,
    DataTableTransformValueColumn,
    DataTableCurrencyColumn,
    DataTableImgRow,
    Draggable,
    DataTableStatusColumn,
    SelectFilterList,
    FormGroup,
    DataTableBodyComponents,
    ContextMenuItem,
    ContextMenu,
  },
  mixins: [ContextMenuMixin],
  props: {
    data: {
      type: Array,
      required: true,
    },
    bgExtension: {
      type: String,
      default: '',
    },
    headers: {
      type: Array,
      required: true,
    },
    tableName: {
      type: String,
      default: '',
    },
    hideEdit: {
      type: Boolean,
      default: false,
    },
    hideDelete: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    disableSetQueryString: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    actionOptions: {
      type: Array,
      default() {
        return [];
      },
    },
    selectedRows: {
      type: Array,
      default: null,
    },
    select: {
      type: Boolean,
      default: false,
    },
    selectListFilter: {
      type: Array,
      default() {
        return [];
      },
    },
    hideTableBottom: {
      type: Boolean,
      default: false,
    },
    showExpand: {
      type: Boolean,
    },
    expandComponent: {
      type: Object,
      default() {
        return {};
      },
    },
    contextMenu: {
      type: Object,
      default() {
        return {};
      },
    },
    noItemsText: {
      type: String,
      default: 'Nenhum item encontrado',
    },
  },
  data(vm) {
    return {
      pagination: {
        page: parseInt(vm.$route.query.page, 10) || 1,
        limit: parseInt((
          vm.$route.query.PerPage
          || vm.$route.query.size
        ), 10) || 15,
      },
      selectedHeaders: [],
      selectRows: [],
      statusColumnDefaultMsg: 'DataTableStatusColumn missing a property:',
      selectAllRows: false,
      totalToShow: 0,
      allColumnWidth: true,
      isExpanded: false,
      expanded: [],
    };
  },
  computed: {
    isContextMenu() {
      const context = this.contextMenu;
      if (context) {
        if (!context.show) {
          return context.show;
        }
        const hasItems = Array.isArray(context.items) && context.items.length >= 1;

        if (!hasItems && this.hideEdit && this.hideDelete) {
          return false;
        }
        return context.show;
      }
      return context ? context.show : true;
    },
    contextItems() {
      const { items } = this.contextMenu;

      if (Array.isArray(items) && items.length >= 1) {
        return items;
      }
      return [
        {
          icon: 'eye',
          name: 'Visualizar',
          handler: (row) => this.onClickEdit(row),
          show: !this.hideEdit,
        },
        {
          icon: 'delete',
          name: 'Deletar',
          handler: (row) => this.onClickEdit(row),
          show: !this.hideDelete,
        },
        ...this.actionOptions,
      ];
    },
    isActionsHeader() {
      return (header) => header && (
        header.actions || header.value === 'data_table_actions'
      );
    },
    getBool() {
      return (bool) => {
        if (typeof bool === 'boolean') {
          return bool;
        }
        return true;
      };
    },
    isSelect() {
      return (header) => header
      && (header.select || header.value === 'data-table-select');
    },
    getHeaderValue() {
      return (header) => header.value;
    },
    isHeader() {
      return (header, value) => header.value === value;
    },
    findRowItemIndex() {
      return (row) => this.data.findIndex((item) => item === row);
    },
    isLoading() {
      return this.loading;
    },
    pages() {
      if (this.pagination.limit == null || this.totalItems == null) return 0;

      return Math.ceil(this.totalItems / this.pagination.limit);
    },
    totalItemsText() {
      const { pagination } = this;
      const actualRows = pagination.limit * (pagination.page - 1) + 1;
      let show = pagination.limit * pagination.page;
      if (show > this.totalItems) {
        show = this.totalItems;
      }

      return `${actualRows} - ${show}`;
    },
    tableHeight() {
      return !this.data.length ? 'empty-table-height' : '';
    },
    showSelect() {
      return this.select && this.selectRows.length > 0;
    },
    getSelectedRows() {
      return this.selectedRows;
    },
    getSelectList() {
      return this.selectListFilter.length;
    },
    showActions() {
      return !(this.hideEdit && this.hideDelete && this.actionOptions.length === 0);
    },
    computedClass() {
      return (header) => (this.allColumnWidth && header && header.tooltip
        ? 'isColumnTooltip'
        : (header.class || ''));
    },
    isEmptyNotExpand() {
      return (props) => this.isEmptyProps(props)
      && !this.isPropsMobile(props) && !this.showExpand;
    },
    isPropsMobile() {
      return (props) => props && props.isMobile;
    },
    isEmptyProps() {
      return (props) => props.items && props.items.length;
    },
    isEmptyNotExpandMobile() {
      return (props) => this.isEmptyProps(props)
      && this.isPropsMobile(props) && !this.showExpand;
    },
    isEmptyExpand() {
      return (props) => this.isEmptyProps(props)
      && !this.isPropsMobile(props) && this.showExpand;
    },
    isEmptyExpandMobile() {
      return (props) => this.isEmptyProps(props)
      && this.isPropsMobile(props) && this.showExpand;
    },
  },
  watch: {
    'pagination.page': {
      handler(value) {
        this.updatePage(value);
      },
    },
    'pagination.limit': {
      handler(value) {
        this.$emit('update-rows-per-page', value);
        this.setTotalsRowsToShow();
      },
    },
    '$route.query': {
      handler(value) {
        if (value.page && !this.disableSetQueryString) {
          this.pagination.page = parseInt(value.page, 10);
        }
      },
    },
    totalToShow: {
      handler(value) {
        this.$emit('totals-select', value);
      },
    },
  },
  beforeUpdate() {
    if (this.getSelectedRows) {
      this.selectRows = this.getSelectedRows;
    }
  },
  created() {
    const localHeaders = DataTableService.getHeaders(this.tableName);

    const allHeaders = [
      ...this.headers,
    ];

    if (this.showActions) {
      allHeaders.push({
        text: 'Ações',
        actions: true,
        value: 'data_table_actions',
        sortDir: false,
      });
    }

    if (this.showExpand) {
      allHeaders.push({
        sortDir: false,
        expanded: true,
        value: 'data-table-expand',
        name: 'expanded-header',
        text: 'Expandir',
      });
    }

    if (this.select) {
      allHeaders.unshift({
        sortDir: false,
        select: true,
        value: 'data-table-select',
        text: '',
      });
    }

    if (Array.isArray(localHeaders) && localHeaders.length >= 1) {
      this.selectedHeaders = localHeaders;
      const newHeaders = localHeaders
        .map((header) => {
          const headerCorrespondente = allHeaders.find(
            (item) => item.text === header.text,
          );
          if (!headerCorrespondente) {
            return null;
          }
          return {
            ...headerCorrespondente,
            ...header,
          };
        })
        .filter((item) => item !== null);

      this.selectedHeaders = newHeaders;
    } else {
      this.selectedHeaders = allHeaders;
    }
  },
  methods: {
    contextMenuClick(item, row) {
      if (item) {
        if (typeof item.handler === 'function') {
          return item.handler(row, this);
        }
        return row;
      }
      return item;
    },
    updatedPage(ev) {
      this.$emit('updated-page-by-table', ev);
    },
    updatePage(value) {
      this.$emit('update-page', value);
    },
    onClickEdit(item) {
      this.$emit('edit', item);
    },
    onClickDelete(item) {
      this.$emit('delete', item);
    },
    generateKey(index, value, prefix = '') {
      return `${prefix}${
        index * this.pagination.page + this.pagination.limit + value
      }`;
    },
    getPropByString(obj, path, defaultValue = '') {
      return Get(obj, path, defaultValue);
    },
    headersOrderUpdated() {
      DataTableService.saveHeaders(this.tableName, this.selectedHeaders);
    },
    orderUpdated(event) {
      this.$emit('update-order', event);
    },
    getActionOptionName(action, row) {
      if (typeof action.name === 'function') {
        return action.name(row);
      }
      return action.name;
    },
    getActionOptionIcon(action, row) {
      if (typeof action.icon === 'function') {
        return action.icon(row);
      }
      return action.icon;
    },
    selected() {
      this.$emit('select', this.selectRows);
    },
    selectedAll(value) {
      this.$emit('select-all', value);
    },
    filterSelectList({ property, value }) {
      this.setSelectRowsByFilter(property, value);
      this.selected();
      this.checkIfIsAllSelect();
    },
    setSelectRowsByFilter(property, value) {
      this.selectRows = this.selectRows.map(
        ({ row }) => this.setBodySelectRow(row[property] === value, row),
      );
    },
    setBodySelectRow(rule, row) {
      return {
        row,
        value: rule,
      };
    },
    checkIfIsAllSelect() {
      const { length } = this.selectRows.filter(({ value }) => value);

      this.selectAllRows = length === this.pagination.limit;
    },
    handleValueInAllRows(value) {
      this.selectedAll(value);
      this.selectRows = this.selectRows.map(({ row }) => ({
        value,
        row,
      }));
      this.$nextTick(
        () => {
          this.selected();
        },
      );
    },
    setTotalsRowsToShow() {
      this.totalToShow = Number(this.totalItemsText.split('-')[1]);
    },
    displayColumn(header) {
      if (header.action) {
        return this.showActions;
      } if (this.isSelect(header)) {
        return this.showSelect;
      }
      if (header.expanded || header.value === 'data-table-expand') {
        return this.showExpand;
      }
      return true;
    },
    handleAllColumnWidth(value) {
      this.allColumnWidth = value;
    },
    print(any, msg = '') {
      console.log(msg, any);
    },
  },
};
</script>

<style lang="sass">
.expand_shadow
  box-shadow: inset 0 4px 8px -5px rgb(50 50 50 / 75%), inset 0 -4px 8px -5px rgb(50 50 50 / 75%)
.theme--light.v-sheet
  color: #999999

.header-draggable
  cursor: grab

.rowsPerPageSelect
  width: 60px

.sortable-drag
  color: rgba(0,0,0,0.54)
  font-weight: normal

.empty-table-height > .v-data-table__wrapper > table
  height: calc(100vh - 430px)

#no-items
  height: 225px
  text-align: center

#no-items td
  font-style: normal
  font-weight: normal
  font-size: 16px
  line-height: 24px
  text-align: center
  letter-spacing: 0.005em
  color: secondary300

td.isColumnTooltip
  margin: 0 auto
  max-width: 12rem!important
  text-overflow: ellipsis!important
  overflow: hidden!important
  white-space: nowrap
  text-align: left

table td, table th
  font-size: clamp(.5rem, .75rem, 1rem)!important
  padding: 0 .4rem!important

table
  overflow: visible!important
  box-sizing: border-box!important

.v-expansion-panel--active:not(:first-child), .v-expansion-panel--active + .v-expansion-panel
  margin-top: 0!important

</style>
